// Search Types and Interfaces for 1vX.gg

export type SearchResultType = 'article' | 'team' | 'player' | 'tournament' | 'match';

export interface SearchFilters {
  contentType?: SearchResultType[];
  dateRange?: {
    from?: string;
    to?: string;
  };
  author?: string[];
  game?: string[];
  tags?: string[];
}

export interface SearchResult {
  id: string;
  type: SearchResultType;
  title: string;
  excerpt: string;
  url: string;
  publishedDate?: string;
  author?: string;
  tags: string[];
  relevanceScore: number;
  highlightedFields: Record<string, string>;

  // Type-specific fields
  // For articles
  category?: 'news' | 'review' | 'esports';
  coverImage?: { url: string; title?: string; description?: string };

  // For teams
  country?: string;
  game?: { name: string; image?: { url: string } };
  players?: string[];
  achievements?: string[];
  logo?: { url: string; title?: string; description?: string };

  // For players
  gamertag?: string;
  realName?: string;
  team?: string;
  role?: string;
  photo?: { url: string; title?: string; description?: string };
  currentTeam?: { id: string; name: string; slug: string; logo?: { url: string } };

  // For tournaments
  startDate?: string;
  endDate?: string;
  format?: string;
  prizePool?: number;
  image?: { url: string; title?: string; description?: string };

  // For matches
  teams?: any[];
}

export interface SearchResponse {
  query: string;
  total: number;
  page: number;
  limit: number;
  results: SearchResult[];
  suggestions?: string[];
  filters: SearchFilters;
  executionTime?: number;
}

export interface SearchSuggestion {
  text: string;
  type?: SearchResultType;
  count?: number;
}

export interface SearchRequest {
  query: string;
  filters?: SearchFilters;
  page?: number;
  limit?: number;
  includeHighlights?: boolean;
  includeSuggestions?: boolean;
}

// Database types for search index
export interface SearchIndexRecord {
  id: string;
  content_type: SearchResultType;
  content_id: string;
  title: string;
  content: string;
  excerpt?: string;
  url: string;
  published_date?: string;
  author?: string;
  tags: string[];
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

// Analytics event types
export interface SearchAnalytics {
  search_performed: {
    query: string;
    results_count: number;
    execution_time?: number;
    content_type_filter?: string;
    user_id?: string;
    session_id?: string;
  };

  search_result_clicked: {
    query: string;
    result_id: string;
    result_type: SearchResultType;
    result_title: string;
    position: number;
    user_id?: string;
    session_id?: string;
  };

  search_no_results: {
    query: string;
    content_type_filter?: string;
    user_id?: string;
    session_id?: string;
  };

  search_suggestion_clicked: {
    suggestion: string;
    original_query: string;
    user_id?: string;
    session_id?: string;
  };
}

// Service method options
export interface SearchOptions {
  enableAnalytics?: boolean;
  enableCache?: boolean;
  cacheTimeout?: number;
  userId?: string;
  sessionId?: string;
}

// Error types
export class SearchError extends Error {
  constructor(
    message: string,
    public code: string,
    public statusCode: number = 500,
  ) {
    super(message);
    this.name = 'SearchError';
  }
}

export class SearchValidationError extends SearchError {
  constructor(message: string) {
    super(message, 'VALIDATION_ERROR', 400);
  }
}

export class SearchServiceError extends SearchError {
  constructor(message: string) {
    super(message, 'SERVICE_ERROR', 500);
  }
}
